import React, { useState, useEffect, useRef } from 'react'
import { Landmark, Upload, CheckCircle, FileText, RefreshCw, Eye, EyeOff, Copy } from 'lucide-react'
import { HubTemplate } from './HubTemplate'
import { CertifIDVerification } from './CertifIDVerification'
import toast from 'react-hot-toast'
import { useAuth } from '../../contexts/AuthContext'
import { useExchangeHubStatus } from '../../hooks/useExchangeHubStatus'
import { useHubAccess } from '../../hooks/useHubAccess'
import { activityLog } from '../../services/activityLog'
import { documentExtraction } from '../../services/documentExtraction'
import { wizardApi } from '../../services/api/wizard'
import { DEMO1_QI_WIRE } from '@open1031/shared/demo/demo1'
import { useConfirm } from '../../contexts/ConfirmProvider'

type InputMethod = 'api' | 'upload'
type SyncState = 'idle' | 'syncing' | 'synced'
type UploadVerificationState = 'idle' | 'verifying' | 'verified'

const STEP_ID = 'verify-funding'

const MOCK_ACCOUNT_FULL = DEMO1_QI_WIRE.accountNumber
const MOCK_ACCOUNT_MASKED = `•••• ${DEMO1_QI_WIRE.accountNumber.slice(-4)}`
const MOCK_ROUTING_FULL = DEMO1_QI_WIRE.abaRouting
const MOCK_ROUTING_MASKED = `•••• ${DEMO1_QI_WIRE.abaRouting.replace(/\s/g, '').slice(-4)}`

function loadSalesPriceFromStorage(exchangeId: string): number {
  try {
    for (const scope of ['phase1', 'initial'] as const) {
      const raw = localStorage.getItem(`sale_wizard_v1_${exchangeId}_${scope}`)
      if (raw) {
        const parsed = JSON.parse(raw) as { listPrice?: string | number }
        const numeric = typeof parsed.listPrice === 'number'
          ? parsed.listPrice
          : parseFloat(String(parsed.listPrice ?? '').replace(/[^0-9.]/g, ''))
        if (Number.isFinite(numeric) && numeric > 0) return numeric
      }
    }
  } catch { }
  try {
    const raw = localStorage.getItem('calculator_results')
    if (raw) {
      const parsed = JSON.parse(raw) as { input?: { sellingPrice?: number } }
      const price = typeof parsed?.input?.sellingPrice === 'number' ? parsed.input.sellingPrice : 0
      if (price > 0) return price
    }
  } catch { }
  return 0
}

function loadPrincipalAmountFromStorage(userId: string): number {
  try {
    const raw = localStorage.getItem(`escrow_data_${userId}`)
    if (raw) {
      const parsed = JSON.parse(raw) as { principalAmount?: number }
      if (typeof parsed.principalAmount === 'number') return parsed.principalAmount
    }
  } catch { }
  return loadSalesPriceFromStorage(userId)
}

export const EscrowHub: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { user } = useAuth()
  const { isHubSubmitted, updateStatus, exchangeId, status } = useExchangeHubStatus()
  const access = useHubAccess('escrow', user)
  const confirm = useConfirm()

  const [submitted, setSubmitted] = useState(() => isHubSubmitted('escrow'))
  const [confirmed, setConfirmed] = useState(() => {
    const persisted = (status?.escrowData as { escrowConfirmed?: boolean } | null)?.escrowConfirmed
    return persisted === true
  })
  const [inputMethod, setInputMethod] = useState<InputMethod>(() => {
    const persisted = (status?.escrowData as { escrowInputMethod?: InputMethod } | null)?.escrowInputMethod
    return persisted === 'upload' ? 'upload' : 'api'
  })
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [uploadVerification, setUploadVerification] = useState<UploadVerificationState>('idle')
  const [syncState, setSyncState] = useState<SyncState>('idle')
  const [syncedBalance, setSyncedBalance] = useState<number>(0)
  const [extractedBalance, setExtractedBalance] = useState<number>(0)
  const [showAccount, setShowAccount] = useState(false)
  const [showRouting, setShowRouting] = useState(false)
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [principalAmount, setPrincipalAmount] = useState<number>(() => {
    if (!user?.id) return 0
    return loadPrincipalAmountFromStorage(user.id)
  })

  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => { document.body.classList.remove('modal-open') }
  }, [])

  useEffect(() => {
    if (!exchangeId) return
    let cancelled = false
    wizardApi.getEscrow(exchangeId).then((state) => {
      if (cancelled) return
      if (state?.exchangeFundsHeld != null) {
        setPrincipalAmount(state.exchangeFundsHeld)
        if (user?.id) {
          localStorage.setItem(
            `escrow_data_${user.id}`,
            JSON.stringify({ principalAmount: state.exchangeFundsHeld, fundedAt: state.exchangeFundsHeldAt }),
          )
        }
        return
      }
      const fromWizard = loadSalesPriceFromStorage(exchangeId)
      if (fromWizard > 0) setPrincipalAmount(fromWizard)
    })
    return () => { cancelled = true }
  }, [exchangeId, user?.id])

  const persistEscrowBalance = async (balance: number) => {
    const fundedAt = new Date().toISOString()
    if (user?.id) {
      localStorage.setItem(
        `escrow_data_${user.id}`,
        JSON.stringify({ principalAmount: balance, fundedAt }),
      )
      window.dispatchEvent(new CustomEvent('escrowDataUpdated'))
    }
    if (exchangeId) {
      try {
        await wizardApi.saveEscrow(exchangeId, {
          exchangeFundsHeld: balance,
          exchangeFundsHeldAt: fundedAt,
          exchangeFundsStatus: 'deposited',
        })
        setLastSavedAt(fundedAt)
      } catch (error) {
        console.warn('[EscrowHub] failed to persist escrow to DB; localStorage cache retained:', error)
      }
    }
  }

  useEffect(() => {
    const handler = () => setSubmitted(true)
    window.addEventListener('escrowHubCompleted', handler)
    return () => window.removeEventListener('escrowHubCompleted', handler)
  }, [])

  useEffect(() => {
    const check = () => {
      const hubSubmitted = isHubSubmitted('escrow')
      if (hubSubmitted !== submitted) setSubmitted(hubSubmitted)
    }
    check()
    const interval = setInterval(check, 2000)
    return () => clearInterval(interval)
  }, [isHubSubmitted, submitted])

  useEffect(() => {
    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current)
    }
  }, [])

  const salesPrice = loadSalesPriceFromStorage(exchangeId)

  const handleBankSync = () => {
    if (syncState !== 'idle') return
    setSyncState('syncing')
    syncTimerRef.current = setTimeout(() => {
      const balance = salesPrice > 0 ? salesPrice : principalAmount > 0 ? principalAmount : 0
      if (balance === 0) {
        console.warn('[EscrowHub] salesPrice not found in sales wizard or calculator_results; sync balance is $0')
      }
      setSyncedBalance(balance)
      setSyncState('synced')
      persistEscrowBalance(balance)
    }, 1200)
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null
    if (!file) return
    setUploadedFile(file)
    setUploadVerification('verifying')
    try {
      const extracted = await documentExtraction.simulateExtraction(file, 'other')
      const balance = typeof extracted.escrowBalance === 'number' && extracted.escrowBalance > 0
        ? extracted.escrowBalance
        : salesPrice > 0 ? salesPrice : principalAmount > 0 ? principalAmount : 0
      if (typeof extracted.escrowBalance !== 'number' || extracted.escrowBalance === 0) {
        console.warn('[EscrowHub] No escrowBalance in extracted data; falling back to salesPrice mock')
      }
      setExtractedBalance(balance)
      setUploadVerification('verified')
      persistEscrowBalance(balance)
    } catch {
      const balance = salesPrice > 0 ? salesPrice : principalAmount > 0 ? principalAmount : 0
      setExtractedBalance(balance)
      setUploadVerification('verified')
      persistEscrowBalance(balance)
    }
  }

  const handleSubmit = async () => {
    if (submitted) return
    const balanceToSave = inputMethod === 'api' ? syncedBalance : extractedBalance
    const finalBalance = balanceToSave > 0 ? balanceToSave : principalAmount
    const formattedFinal = finalBalance > 0
      ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(finalBalance)
      : 'Pending receipt'
    const confirmed = await confirm({
      title: 'Acknowledge Escrow Receipt?',
      description: `Confirm that ${formattedFinal} has been received into the Open1031 QI escrow account under §1031 qualified custody. This action cannot be undone.`,
      confirmText: 'Acknowledge',
      cancelText: 'Cancel',
      icon: Landmark,
      iconColor: 'teal',
    })
    if (!confirmed) return
    const date = new Date().toISOString()
    await persistEscrowBalance(finalBalance)
    await updateStatus({ hubSubmissions: { escrow: { submitted: true, date } } })
    localStorage.setItem('escrow_hub_submitted', 'true')
    localStorage.setItem('escrow_hub_submitted_date', date)
    activityLog.logEscrowAcknowledged(finalBalance)
    window.dispatchEvent(new CustomEvent('escrowHubCompleted'))
    window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
    setSubmitted(true)
    toast.success('Escrow acknowledged. Purchase Hub is now unlocked.')
    onClose()
  }

  const handleConfirmationChange = (checked: boolean) => {
    setConfirmed(checked)
    const savedAt = new Date().toISOString()
    setLastSavedAt(savedAt)
    updateStatus({
      escrowData: {
        ...(status?.escrowData as object | null ?? {}),
        escrowConfirmed: checked,
      },
    }).catch(() => undefined)
  }

  const handleInputMethodChange = (method: InputMethod) => {
    setInputMethod(method)
    const savedAt = new Date().toISOString()
    setLastSavedAt(savedAt)
    updateStatus({
      escrowData: {
        ...(status?.escrowData as object | null ?? {}),
        escrowInputMethod: method,
      },
    }).catch(() => undefined)
  }

  const handleCopyWireInstructions = async () => {
    const exchangeRef = exchangeId && exchangeId !== 'anon' ? exchangeId.slice(0, 8) : 'DEMO-001'
    const wireText = [
      'Open1031 QI Wire Instructions',
      `Beneficiary: ${DEMO1_QI_WIRE.beneficiaryName}`,
      `Beneficiary Address: ${DEMO1_QI_WIRE.beneficiaryAddress}`,
      `Bank: ${DEMO1_QI_WIRE.bankName}`,
      `Bank Address: ${DEMO1_QI_WIRE.bankAddress}`,
      `Account Number: ${MOCK_ACCOUNT_FULL}`,
      `ABA Routing Number: ${MOCK_ROUTING_FULL}`,
      `SWIFT/BIC: ${DEMO1_QI_WIRE.swiftBic}`,
      `Reference: Exchange #${exchangeRef}`,
    ].join('\n')
    try {
      await navigator.clipboard.writeText(wireText)
    } catch {
      toast.error('Failed to copy — please copy manually')
      return
    }
    toast.success('Wire instructions copied')
  }

  const displayBalance = (() => {
    if (inputMethod === 'api' && syncState === 'synced') return syncedBalance
    if (inputMethod === 'upload' && uploadVerification === 'verified') return extractedBalance
    return principalAmount
  })()

  const formattedBalance = displayBalance > 0
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(displayBalance)
    : 'Pending receipt'

  const apiMethodReady = syncState === 'synced'
  const uploadMethodReady = uploadVerification === 'verified'
  const methodReady = inputMethod === 'api' ? apiMethodReady : uploadMethodReady

  const completionPercent = (() => {
    if (submitted) return 100
    if (inputMethod === 'api') {
      if (syncState === 'synced') return confirmed ? 80 : 60
      if (syncState === 'syncing') return 30
      return 0
    }
    if (uploadVerification === 'verified') return confirmed ? 80 : 60
    if (uploadVerification === 'verifying') return 40
    return 0
  })()

  const exchangeRef = exchangeId && exchangeId !== 'anon' ? exchangeId.slice(0, 8) : 'DEMO-001'

  const stepContent = (
    <div className="space-y-4 max-w-2xl mx-auto">
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <Landmark className="h-4 w-4 text-cyan-500" />
          <div>
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{DEMO1_QI_WIRE.bankName}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Trust Services &amp; Specialty Banking</p>
          </div>
          {(syncState === 'synced' || uploadVerification === 'verified') && (
            <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-xs font-semibold">
              <CheckCircle className="h-3 w-3" />
              Verified
            </span>
          )}
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">Account type</span>
          <span className="font-medium text-slate-900 dark:text-slate-100">QI Custodial Trust Account</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">Account</span>
          <span className="inline-flex items-center gap-2">
            <span className="font-mono font-medium text-slate-900 dark:text-slate-100">
              {showAccount ? MOCK_ACCOUNT_FULL : MOCK_ACCOUNT_MASKED}
            </span>
            <button
              type="button"
              onClick={() => setShowAccount(v => !v)}
              aria-label={showAccount ? 'Hide account number' : 'Show account number'}
              className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              {showAccount
                ? <EyeOff className="h-3.5 w-3.5 text-slate-500" />
                : <Eye className="h-3.5 w-3.5 text-slate-500" />}
            </button>
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">Routing</span>
          <span className="inline-flex items-center gap-2">
            <span className="font-mono font-medium text-slate-900 dark:text-slate-100">
              {showRouting ? MOCK_ROUTING_FULL : MOCK_ROUTING_MASKED}
            </span>
            <button
              type="button"
              onClick={() => setShowRouting(v => !v)}
              aria-label={showRouting ? 'Hide routing number' : 'Show routing number'}
              className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              {showRouting
                ? <EyeOff className="h-3.5 w-3.5 text-slate-500" />
                : <Eye className="h-3.5 w-3.5 text-slate-500" />}
            </button>
          </span>
        </div>
        {(syncState === 'synced' || uploadVerification === 'verified') && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600 dark:text-slate-400">Balance</span>
            <span className="font-semibold text-cyan-700 dark:text-cyan-300">{formattedBalance}</span>
          </div>
        )}
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-600 dark:text-slate-400">Insurance</span>
          <span className="text-slate-900 dark:text-slate-100">FDIC-insured up to $250,000</span>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 space-y-3">
        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Wire Instructions</p>
        <div className="space-y-2">
          <div className="flex items-start justify-between text-sm gap-4">
            <span className="text-slate-600 dark:text-slate-400 shrink-0">Beneficiary</span>
            <span className="text-slate-900 dark:text-slate-100 text-right">{DEMO1_QI_WIRE.beneficiaryName}</span>
          </div>
          <div className="flex items-start justify-between text-sm gap-4">
            <span className="text-slate-600 dark:text-slate-400 shrink-0">Beneficiary Address</span>
            <span className="text-slate-900 dark:text-slate-100 text-right">{DEMO1_QI_WIRE.beneficiaryAddress}</span>
          </div>
          <div className="flex items-start justify-between text-sm gap-4">
            <span className="text-slate-600 dark:text-slate-400 shrink-0">Bank</span>
            <span className="text-slate-900 dark:text-slate-100 text-right">{DEMO1_QI_WIRE.bankName}</span>
          </div>
          <div className="flex items-start justify-between text-sm gap-4">
            <span className="text-slate-600 dark:text-slate-400 shrink-0">Bank Address</span>
            <span className="text-slate-900 dark:text-slate-100 text-right">{DEMO1_QI_WIRE.bankAddress}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600 dark:text-slate-400 shrink-0">Account Number</span>
            <span className="inline-flex items-center gap-2">
              <span className="font-mono font-medium text-slate-900 dark:text-slate-100">
                {showAccount ? MOCK_ACCOUNT_FULL : MOCK_ACCOUNT_MASKED}
              </span>
              <button
                type="button"
                onClick={() => setShowAccount(v => !v)}
                aria-label={showAccount ? 'Hide account number' : 'Show account number'}
                className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                {showAccount
                  ? <EyeOff className="h-3.5 w-3.5 text-slate-500" />
                  : <Eye className="h-3.5 w-3.5 text-slate-500" />}
              </button>
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600 dark:text-slate-400 shrink-0">ABA Routing Number</span>
            <span className="inline-flex items-center gap-2">
              <span className="font-mono font-medium text-slate-900 dark:text-slate-100">
                {showRouting ? MOCK_ROUTING_FULL : MOCK_ROUTING_MASKED}
              </span>
              <button
                type="button"
                onClick={() => setShowRouting(v => !v)}
                aria-label={showRouting ? 'Hide routing number' : 'Show routing number'}
                className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                {showRouting
                  ? <EyeOff className="h-3.5 w-3.5 text-slate-500" />
                  : <Eye className="h-3.5 w-3.5 text-slate-500" />}
              </button>
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600 dark:text-slate-400 shrink-0">SWIFT/BIC</span>
            <span className="font-mono font-medium text-slate-900 dark:text-slate-100">{DEMO1_QI_WIRE.swiftBic}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600 dark:text-slate-400 shrink-0">Reference / Memo</span>
            <span className="font-mono font-medium text-slate-900 dark:text-slate-100">
              Exchange #{exchangeRef}
            </span>
          </div>
        </div>
        <div className="flex justify-end pt-1">
          <button
            type="button"
            onClick={handleCopyWireInstructions}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
          >
            <Copy className="h-3.5 w-3.5" />
            Copy wire instructions
          </button>
        </div>
      </div>

      <CertifIDVerification context="escrow-inbound" />

      <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        <button
          type="button"
          onClick={() => handleInputMethodChange('api')}
          className={`flex-1 py-2 text-xs font-semibold transition-colors ${
            inputMethod === 'api'
              ? 'bg-cyan-600 text-white'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
          }`}
        >
          Bank Sync
        </button>
        <button
          type="button"
          onClick={() => handleInputMethodChange('upload')}
          className={`flex-1 py-2 text-xs font-semibold transition-colors ${
            inputMethod === 'upload'
              ? 'bg-cyan-600 text-white'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
          }`}
        >
          Upload Confirmation
        </button>
      </div>

      {inputMethod === 'api' && (
        <div className="rounded-lg border border-cyan-200 dark:border-cyan-800 bg-cyan-50/60 dark:bg-cyan-900/10 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RefreshCw className={`h-4 w-4 text-cyan-500 ${syncState === 'syncing' ? 'animate-spin' : ''}`} />
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Balance synced from {DEMO1_QI_WIRE.bankName}
              </p>
            </div>
            {syncState === 'synced' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 text-xs font-semibold">
                <CheckCircle className="h-3 w-3" />
                Synced
              </span>
            )}
          </div>
          {syncState === 'idle' && (
            <button
              type="button"
              onClick={handleBankSync}
              className="w-full py-2 px-4 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold transition-colors"
              aria-label={`Sync balance from ${DEMO1_QI_WIRE.bankName}`}
            >
              Sync Balance
            </button>
          )}
          {syncState === 'syncing' && (
            <div className="flex items-center gap-2 py-1" role="status" aria-label="Syncing balance">
              <p className="text-sm text-slate-600 dark:text-slate-400">Connecting to {DEMO1_QI_WIRE.bankName}…</p>
            </div>
          )}
          {syncState === 'synced' && (
            <>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-400">Fetched balance</span>
                <span className="font-semibold text-cyan-700 dark:text-cyan-300">{formattedBalance}</span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Synced from {DEMO1_QI_WIRE.bankName}
              </p>
            </>
          )}
        </div>
      )}

      {inputMethod === 'upload' && (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 space-y-3">
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Upload your QI escrow account confirmation
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Accepts PDF or image. Balance and bank details will be extracted automatically.
          </p>

          {uploadVerification === 'idle' && (
            <div>
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={handleFileChange}
                className="sr-only focus-visible:ring-cyan-400"
                aria-label="Upload escrow confirmation document"
                id="escrow-doc-upload"
              />
              <label
                htmlFor="escrow-doc-upload"
                className="flex flex-col items-center gap-2 p-6 border-2 border-dashed border-cyan-300 dark:border-cyan-600 rounded-lg cursor-pointer hover:border-cyan-400 dark:hover:border-cyan-500 focus-within:ring-2 focus-within:ring-cyan-400 transition-colors"
              >
                <Upload className="h-8 w-8 text-slate-400" />
                <span className="text-sm text-slate-600 dark:text-slate-400 font-medium">
                  Click to upload confirmation document
                </span>
                <span className="text-xs text-slate-400">PDF, PNG, or JPG</span>
              </label>
            </div>
          )}

          {uploadVerification === 'verifying' && (
            <div className="flex flex-col items-center gap-3 py-4" role="status" aria-label="Verifying document">
              <div className="relative h-10 w-10">
                <div className="absolute inset-0 rounded-full border-2 border-cyan-200 dark:border-cyan-800"></div>
                <div className="absolute inset-0 rounded-full border-t-2 border-cyan-500 animate-spin"></div>
              </div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Verifying…
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Extracting balance and bank details from your document
              </p>
            </div>
          )}

          {uploadVerification === 'verified' && uploadedFile && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-green-700 dark:text-green-300">Document Verified</p>
                  <p className="text-xs text-green-600 dark:text-green-400 truncate">{uploadedFile.name}</p>
                </div>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-xs font-semibold flex-shrink-0">
                  <FileText className="h-3 w-3" />
                  Verified
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-400">Extracted balance</span>
                <span className="font-semibold text-cyan-700 dark:text-cyan-300">{formattedBalance}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-400">Bank</span>
                <span className="text-slate-900 dark:text-slate-100">{DEMO1_QI_WIRE.bankName}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-400">Account</span>
                <span className="font-mono text-slate-900 dark:text-slate-100">{MOCK_ACCOUNT_MASKED}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )

  return (
    <HubTemplate
      icon={Landmark}
      color="cyan"
      title="Escrow Hub"
      subtitle="Sale proceeds in qualified intermediary custody"
      onClose={onClose}
      submitted={submitted}
      completionPercent={completionPercent}
      prerequisitesMet={access.canAccess}
      prerequisiteMessage={access.message ?? 'Complete the Sales Hub first to unlock Escrow.'}
      prerequisiteHubName="Sales Hub"
      activeStepId={STEP_ID}
      onStepChange={() => undefined}
      onSubmit={handleSubmit}
      submitLabel="Acknowledge funds received in escrow"
      completedMessage="Escrow acknowledged"
      nextHubMessage="Purchase Hub is now unlocked"
      lastSavedAt={lastSavedAt}
      showSaveIndicator={true}
      steps={[
        {
          id: STEP_ID,
          label: 'Verify Funding',
          icon: Landmark,
          fieldsComplete: methodReady || submitted,
          confirmation: {
            label: (
              'I confirm my sale proceeds have been received into the Open1031 QI escrow account' +
              ' and are under §1031 qualified custody.'
            ),
            checked: confirmed || submitted,
            onChange: handleConfirmationChange,
          },
          intro: {
            body: (
              `Your sale proceeds are held in a segregated, FDIC-insured QI Custodial Trust Account at ${DEMO1_QI_WIRE.bankName}` +
              ' (coverage up to $250,000). The funds are locked until you identify and close on your replacement property' +
              ' — preserving §1031 tax-deferred status.'
            ),
          },
          content: stepContent,
        },
      ]}
    />
  )
}
